/**
 * Sentry 安全包装工具
 *
 * 确保即使 Sentry 服务挂掉，也不会影响应用的正常运行
 * 所有 Sentry 调用都应该通过这个工具函数，而不是直接调用 Sentry API
 */

import * as Sentry from '@sentry/nextjs';
import { Env } from '@/libs/env';
import { isInApp } from '@/libs/comeAppSDK';
import { getTraceUserIds, getTraceSuffixes } from '@/libs/global-config';

const isSentryEnabled = () => Env.NEXT_PUBLIC_ENABLE_SENTRY !== 'false';

/**
 * 读取上次缓存的 userId（供 Sentry tag 和 traceUserIds 兜底检查使用）
 * 比 Sentry scope 的用户信息获取更早，可使用上次 session 沿用的值
 */
function getLastSeenUserId(): string | null {
  try {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem('last_uid');
  } catch {
    return null;
  }
}

/**
 * 判断当前用户是否在 traceUserIds 动态配置中
 * 优先从 Sentry scope 读取 userId，兜底从 localStorage 的 last_uid 读取
 */
function isCurrentUserTracked(): boolean {
  try {
    const traceUserIds = getTraceUserIds();
    if (traceUserIds.length === 0) return false;

    // 优先从 Sentry scope 读取（API 响应后已由 setSentryUser 设置）
    const user = Sentry.getCurrentScope().getUser() as Record<string, unknown> | undefined;
    const scopeUserId = user?.['userId'] as string | undefined;
    if (scopeUserId && traceUserIds.includes(scopeUserId)) return true;

    // 兜底：使用上次缓存的 userId（跨 session 持久化，早于 API 响应可用）
    const cachedUserId = getLastSeenUserId();
    if (cachedUserId && traceUserIds.includes(cachedUserId)) return true;

    return false;
  } catch {
    return false;
  }
}

/**
 * 取当前用户 ID 的后两位，返回两位字符串（不足两位左补 '0'）
 * 优先从 Sentry scope 读取，兜底从 localStorage last_uid 读取
 */
function getUserIdSuffix(): string | null {
  try {
    const user = Sentry.getCurrentScope().getUser() as Record<string, unknown> | undefined;
    const userId = (user?.['userId'] as string | undefined) || getLastSeenUserId();
    if (!userId) return null;
    // 取最后两位并左补 '0'，确保始终是两位字符串（如 userId="7" → "07"）
    return userId.slice(-2).padStart(2, '0');
  } catch {
    return null;
  }
}

/**
 * 判断当前用户是否命中 traceSuffixes 后两位采样桶
 * 命中的用户将被升级为全采样（_force_send），覆盖约 10% 的用户群体
 */
function isCurrentUserSampledBySuffix(): boolean {
  try {
    const suffixes = getTraceSuffixes();
    if (suffixes.length === 0) return false;
    const suffix = getUserIdSuffix();
    if (suffix === null) return false;
    return suffixes.includes(suffix);
  } catch {
    return false;
  }
}

/**
 * 设置 Sentry 用户信息
 * 应在用户登录成功后调用
 * 注意：不会覆盖已设置的 deviceId，而是添加额外的用户信息字段
 */
export function setSentryUser(userId: string, userName?: string): void {
  if (!isSentryEnabled()) {
    return;
  }

  try {
    // 获取当前 scope 并更新用户信息（保留已有的 deviceId）
    Sentry.getCurrentScope().setUser((currentUser: Sentry.User | null) => ({
      ...currentUser,
      userId,        // 外显 userId
      username: userName,
    }));
  } catch (sentryError) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('⚠️  Sentry set user failed:', sentryError);
    }
  }
}

/**
 * 清除 Sentry 用户信息
 * 应在用户登出时调用
 * 注意：只清除用户相关字段，保留 deviceId
 */
export function clearSentryUser(): void {
  if (!isSentryEnabled()) {
    return;
  }

  try {
    // 保留 deviceId，只清除用户相关字段
    Sentry.getCurrentScope().setUser((currentUser: Sentry.User | null) => ({
      id: currentUser?.id, // 保留 deviceId
      userId: undefined,
      username: undefined,
    }));
  } catch (sentryError) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('⚠️  Sentry clear user failed:', sentryError);
    }
  }
}

/**
 * 安全地捕获异常到 Sentry（普通采样版本）
 *
 * - 自动添加 platform、release、last_uid 标记，方便问题定位
 * - 若当前用户在 global-config 的 traceUserIds 中，自动升级为全采样（_force_send）
 */
export function safeCaptureException(
  error: Error,
  context?: Parameters<typeof Sentry.captureException>[1]
): void {
  if (!isSentryEnabled()) {
    return;
  }

  try {
    const platform = isInApp() ? 'APP' : 'H5';
    const release = Env.RELEASE;
    const lastUid = getLastSeenUserId();
    const forceSend = isCurrentUserTracked() || isCurrentUserSampledBySuffix();

    Sentry.captureException(error, (scope) => {
      scope.setTag('platform', platform);
      scope.setTag('release', release);
      if (lastUid) scope.setTag('last_uid', lastUid);
      if (forceSend) scope.setTag('_force_send', 'true');

      if (context) {
        if (typeof context === 'function') {
          return context(scope);
        } else {
          scope.update(context);
        }
      }
      return scope;
    });
  } catch (sentryError) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('⚠️  Sentry:', sentryError);
    }
  }
}

/**
 * 全采样版本 captureException
 *
 * - 添加 _force_send tag → beforeSend 无条件放行，100% 上报到 Sentry
 * - 附带 localStorage.accessToken（extra 字段，辅助关联用户身份）
 * - 附带 last_uid tag（早于 API 响应的缓存 userId）
 * - 适用场景：Error Boundary、WalletModal 异常、钱包充提操作错误
 */
export function safeCaptureExceptionFull(
  error: Error,
  context?: Parameters<typeof Sentry.captureException>[1]
): void {
  if (!isSentryEnabled()) {
    return;
  }

  try {
    const platform = isInApp() ? 'APP' : 'H5';
    const release = Env.RELEASE;
    const lastUid = getLastSeenUserId();

    let accessToken: string | null = null;
    try {
      if (typeof window !== 'undefined') {
        accessToken = localStorage.getItem('accessToken');
      }
    } catch { /* ignore */ }

    Sentry.captureException(error, (scope) => {
      scope.setTag('platform', platform);
      scope.setTag('release', release);
      scope.setTag('_force_send', 'true');
      if (lastUid) scope.setTag('last_uid', lastUid);
      if (accessToken) scope.setExtra('accessToken', accessToken);

      if (context) {
        if (typeof context === 'function') {
          return context(scope);
        } else {
          scope.update(context);
        }
      }
      return scope;
    });
  } catch (sentryError) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('⚠️  Sentry:', sentryError);
    }
  }
}

/**
 * 安全地捕获消息到 Sentry
 * 即使 Sentry 服务挂掉或出现错误，也不会影响应用运行
 */
export function safeCaptureMessage(
  message: string,
  level: Sentry.SeverityLevel = 'error'
): void {
  if (!isSentryEnabled()) {
    return;
  }

  try {
    Sentry.captureMessage(message, level);
  } catch (sentryError) {
    // Sentry 调用失败不应该影响应用
    if (process.env.NODE_ENV === 'development') {
      console.warn('⚠️  Sentry:', sentryError);
    }
  }
}

